import { useCallback, useEffect, useRef, useState } from 'react';
import { useOpenCv } from 'opencv-react';
import {
  clamp,
  clampPan as clampPanUtil,
  getBaseCropSize,
  getCropRect as getCropRectUtil,
  getCropSize as getCropSizeUtil,
  PORTRAIT_ASPECT_RATIO,
} from '../../utils/perspectiveCropperUtils';
import { applyPerspectiveToCanvas, createBaseMat, drawPreview } from '../../utils/perspectiveCropperRenderUtils';

type PerspectiveCropperStateChange = { loading?: boolean };

type UsePerspectiveCropperRenderParams = {
  imageUrl: string;
  file: File;
  onChange?: (state: PerspectiveCropperStateChange) => void;
};

type UsePerspectiveCropperRenderResult = {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  previewCanvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  loading: boolean;
  naturalSize: { width: number; height: number } | null;
  zoomRef: React.MutableRefObject<number>;
  panRef: React.MutableRefObject<{ x: number; y: number }>;
  clampPan: () => void;
  getCropRect: () => { x: number; y: number; width: number; height: number } | null;
  renderPreview: () => void;
  backToCrop: () => void;
  done: () => Promise<Blob>;
  setHorizontalTilt: (value: number) => void;
  setVerticalTilt: (value: number) => void;
  setRotation: (value: number) => void;
};

export const usePerspectiveCropperRender = ({
  imageUrl,
  file,
  onChange,
}: UsePerspectiveCropperRenderParams): UsePerspectiveCropperRenderResult => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const baseCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const baseMatRef = useRef<any>(null);
  const previewMatRef = useRef<any>(null);
  const previewSharesBaseRef = useRef(false);

  const { loaded: cvLoaded, cv } = useOpenCv();

  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [loading, setLoading] = useState(true);

  const horizontalTiltRef = useRef(0);
  const verticalTiltRef = useRef(0);
  const transformedCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const transformScaleRef = useRef(1);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const pendingRenderRef = useRef(false);
  const pendingForceTransformRef = useRef(false);
  const renderRafRef = useRef<number | null>(null);
  const rotationRef = useRef(0);

  const notifyChange = useCallback(
    (isLoading: boolean) => {
      onChange?.({ loading: isLoading });
    },
    [onChange],
  );

  const cleanupMats = useCallback(() => {
    if (previewMatRef.current) {
      if (!previewSharesBaseRef.current) {
        previewMatRef.current.delete();
      }
      previewMatRef.current = null;
    }
    previewSharesBaseRef.current = false;
    if (baseMatRef.current) {
      baseMatRef.current.delete();
      baseMatRef.current = null;
    }
  }, []);

  const createBase = useCallback(() => {
    if (!cvLoaded || !cv || !baseCanvasRef.current) {
      return;
    }
    cleanupMats();
    try {
      createBaseMat({
        cv,
        cvLoaded,
        baseCanvas: baseCanvasRef.current,
        baseMatRef,
        previewMatRef,
        previewSharesBaseRef,
      });
    } catch (error) {
      baseMatRef.current = null;
      previewMatRef.current = null;
      previewSharesBaseRef.current = false;
    }
  }, [cleanupMats, cv, cvLoaded]);

  const ensurePreviewCanvasSize = useCallback(() => {
    if (!containerRef.current || !previewCanvasRef.current || !naturalSize) {
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const aspect = PORTRAIT_ASPECT_RATIO;
    let width = rect.width;
    let height = width / aspect;
    if (height > rect.height) {
      height = rect.height;
      width = height * aspect;
    }

    previewCanvasRef.current.width = width;
    previewCanvasRef.current.height = height;
  }, [naturalSize]);

  const getCropSize = useCallback(() => {
    if (!naturalSize) {
      return null;
    }
    const base = getBaseCropSize({
      naturalWidth: naturalSize.width,
      naturalHeight: naturalSize.height,
    });
    return getCropSizeUtil({
      baseWidth: base.width,
      baseHeight: base.height,
      zoom: zoomRef.current,
    });
  }, [naturalSize]);

  const clampPan = useCallback(() => {
    if (!naturalSize) {
      return;
    }
    const cropSize = getCropSize();
    if (!cropSize) {
      return;
    }
    panRef.current = clampPanUtil({
      pan: panRef.current,
      naturalWidth: naturalSize.width,
      naturalHeight: naturalSize.height,
      cropWidth: cropSize.width,
      cropHeight: cropSize.height,
    });
  }, [getCropSize, naturalSize]);

  const getCropRect = useCallback(() => {
    if (!naturalSize) {
      return null;
    }
    const cropSize = getCropSize();
    if (!cropSize) {
      return null;
    }
    return getCropRectUtil({
      naturalWidth: naturalSize.width,
      naturalHeight: naturalSize.height,
      cropWidth: cropSize.width,
      cropHeight: cropSize.height,
      panX: panRef.current.x,
      panY: panRef.current.y,
    });
  }, [getCropSize, naturalSize]);

  const renderPreview = useCallback(
    (options?: { forceTransform?: boolean }) => {
      if (!naturalSize) {
        return;
      }

      let workCanvas = transformedCanvasRef.current;
      if (!workCanvas) {
        workCanvas = document.createElement('canvas');
        transformedCanvasRef.current = workCanvas;
      }

      if (options?.forceTransform || workCanvas.width === 0 || workCanvas.height === 0) {
        applyPerspectiveToCanvas({
          cv,
          cvLoaded,
          naturalWidth: naturalSize.width,
          naturalHeight: naturalSize.height,
          baseCanvas: baseCanvasRef.current,
          baseMatRef,
          previewMatRef,
          previewSharesBaseRef,
          transformScaleRef,
          horizontalTilt: horizontalTiltRef.current,
          verticalTilt: verticalTiltRef.current,
          targetCanvas: workCanvas,
          quality: 'preview',
        });
      }

      drawPreview({
        previewCanvas: previewCanvasRef.current,
        cropRect: getCropRect(),
        transformScale: transformScaleRef.current || 1,
        rotation: rotationRef.current || 0,
        sourceCanvas: workCanvas,
      });
    },
    [cv, cvLoaded, getCropRect, naturalSize],
  );

  const scheduleRender = useCallback(
    (options?: { forceTransform?: boolean }) => {
      pendingRenderRef.current = true;
      if (options?.forceTransform) {
        pendingForceTransformRef.current = true;
      }
      if (renderRafRef.current !== null) {
        return;
      }
      renderRafRef.current = requestAnimationFrame(() => {
        renderRafRef.current = null;
        if (!pendingRenderRef.current) {
          return;
        }
        const force = pendingForceTransformRef.current;
        pendingRenderRef.current = false;
        pendingForceTransformRef.current = false;
        renderPreview(force ? { forceTransform: true } : undefined);
      });
    },
    [renderPreview],
  );

  const backToCrop = useCallback(() => {
    horizontalTiltRef.current = 0;
    verticalTiltRef.current = 0;
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    rotationRef.current = 0;
    clampPan();
    if (naturalSize && previewCanvasRef.current) {
      transformedCanvasRef.current = null;
      scheduleRender({ forceTransform: true });
    }
  }, [clampPan, naturalSize, scheduleRender]);

  const done = useCallback(async () => {
    if (!cvLoaded || !cv || !baseCanvasRef.current || !naturalSize) {
      throw new Error('Perspective tools are not ready');
    }

    const transformedCanvas = document.createElement('canvas');
    applyPerspectiveToCanvas({
      cv,
      cvLoaded,
      naturalWidth: naturalSize.width,
      naturalHeight: naturalSize.height,
      baseCanvas: baseCanvasRef.current,
      baseMatRef,
      previewMatRef,
      previewSharesBaseRef,
      transformScaleRef,
      horizontalTilt: horizontalTiltRef.current,
      verticalTilt: verticalTiltRef.current,
      targetCanvas: transformedCanvas,
      quality: 'full',
    });

    const cropRect = getCropRect();
    if (!cropRect) {
      throw new Error('Unable to determine crop area');
    }

    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = Math.round(cropRect.width);
    outputCanvas.height = Math.round(cropRect.height);

    const ctx = outputCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.drawImage(
      transformedCanvas,
      cropRect.x,
      cropRect.y,
      cropRect.width,
      cropRect.height,
      0,
      0,
      cropRect.width,
      cropRect.height,
    );

    let finalCanvas: HTMLCanvasElement = outputCanvas;
    const rotation = rotationRef.current || 0;
    if (Math.abs(rotation) > 0.0001) {
      const rotationRad = (rotation * Math.PI) / 180;
      const cos = Math.abs(Math.cos(rotationRad));
      const sin = Math.abs(Math.sin(rotationRad));
      const rotatedWidth = Math.round(outputCanvas.width * cos + outputCanvas.height * sin);
      const rotatedHeight = Math.round(outputCanvas.width * sin + outputCanvas.height * cos);
      const rotatedCanvas = document.createElement('canvas');
      rotatedCanvas.width = rotatedWidth;
      rotatedCanvas.height = rotatedHeight;
      const rotatedCtx = rotatedCanvas.getContext('2d');
      if (!rotatedCtx) {
        throw new Error('Failed to rotate perspective crop');
      }
      rotatedCtx.fillStyle = '#fff';
      rotatedCtx.fillRect(0, 0, rotatedWidth, rotatedHeight);
      rotatedCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
      rotatedCtx.rotate(rotationRad);
      rotatedCtx.drawImage(
        outputCanvas,
        -outputCanvas.width / 2,
        -outputCanvas.height / 2,
        outputCanvas.width,
        outputCanvas.height,
      );
      finalCanvas = rotatedCanvas;
    }

    return new Promise<Blob>((resolve, reject) => {
      finalCanvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to export perspective crop'));
          }
        },
        file.type,
        0.95,
      );
    });
  }, [cv, cvLoaded, file.type, getCropRect, naturalSize]);

  const setHorizontalTilt = useCallback((value: number) => {
    horizontalTiltRef.current = clamp(value, -1, 1);
    if (naturalSize && previewCanvasRef.current) {
      scheduleRender({ forceTransform: true });
    }
  }, [naturalSize, scheduleRender]);

  const setVerticalTilt = useCallback((value: number) => {
    verticalTiltRef.current = clamp(value, -1, 1);
    if (naturalSize && previewCanvasRef.current) {
      scheduleRender({ forceTransform: true });
    }
  }, [naturalSize, scheduleRender]);

  const setRotation = useCallback((value: number) => {
    const clampedValue = Math.max(-180, Math.min(180, value));
    if (Math.abs(rotationRef.current - clampedValue) < 0.001) {
      return;
    }
    rotationRef.current = clampedValue;
    if (naturalSize && previewCanvasRef.current) {
      scheduleRender();
    }
  }, [naturalSize, scheduleRender]);

  useEffect(() => {
    return () => {
      if (renderRafRef.current !== null) {
        cancelAnimationFrame(renderRafRef.current);
      }
      cleanupMats();
    };
  }, [cleanupMats]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    notifyChange(true);
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    transformedCanvasRef.current = null;
    transformScaleRef.current = 1;
    rotationRef.current = 0;
    pendingRenderRef.current = false;
    pendingForceTransformRef.current = false;
    if (renderRafRef.current !== null) {
      cancelAnimationFrame(renderRafRef.current);
      renderRafRef.current = null;
    }
    cleanupMats();

    const img = new Image();
    (img as HTMLImageElement).decoding = 'async';
    img.onload = () => {
      if (!mounted) return;

      const baseCanvas = document.createElement('canvas');
      baseCanvas.width = img.naturalWidth || img.width;
      baseCanvas.height = img.naturalHeight || img.height;
      const ctx = baseCanvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(img, 0, 0);
      baseCanvasRef.current = baseCanvas;
      setNaturalSize({ width: baseCanvas.width, height: baseCanvas.height });
      if (cvLoaded) {
        createBase();
      }
      setLoading(false);
      notifyChange(!cvLoaded);
    };
    img.onerror = () => {
      if (!mounted) return;
      setLoading(false);
      notifyChange(false);
    };
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    return () => {
      mounted = false;
    };
  }, [cleanupMats, createBase, cvLoaded, imageUrl, notifyChange]);

  useEffect(() => {
    if (!naturalSize) return;
    ensurePreviewCanvasSize();
  }, [ensurePreviewCanvasSize, naturalSize]);

  useEffect(() => {
    if (!cvLoaded) {
      return;
    }
    createBase();
  }, [createBase, cvLoaded]);

  useEffect(() => {
    if (!naturalSize || !previewCanvasRef.current || !baseCanvasRef.current || !cvLoaded) return;
    scheduleRender({ forceTransform: true });
  }, [cvLoaded, naturalSize, scheduleRender]);

  return {
    containerRef,
    previewCanvasRef,
    loading,
    naturalSize,
    zoomRef,
    panRef,
    clampPan,
    getCropRect,
    renderPreview,
    backToCrop,
    done,
    setHorizontalTilt,
    setVerticalTilt,
    setRotation,
  };
};
